import { RenderIf } from 'react-rainbow-components';
import { METRICS_GROUPS_DEFINITIONS } from '../../constants';
import getGlobalMetricsByGroup from '../../helpers/getGlobalMetricsByGroup';
import {
    BusinessHealthResponse,
    GlobalBusinessHealthMetric,
    PrimaryMetricsPresentation,
} from '../../types';
import DrawerMetricCard from './DrawerMetricCard';
import InsightChannelDrawerSection from './InsightChannelDrawerSection';
import {
    DrawerAggregateSection,
    DrawerBody,
    DrawerHeader,
    DrawerMetricsStack,
    DrawerSubtitle,
    DrawerTitle,
    EmptyHint,
    StyledDrawer,
} from './styled';
import getLabels from '../../BusinessHealthWidget/helpers/getLabels';

export interface InsightDetailDrawerProps {
    isOpen: boolean;
    onRequestClose: () => void;
    metricsGroup: string;
    scoreComponent: string;
    primaryMetrics: string[];
    presentation: PrimaryMetricsPresentation;
    /**
     * Ordered keys for the drawer aggregate metric stack
     * (see `QuestionDefinition.drawerMetricKeys`).
     */
    drawerMetricKeys?: string[];
    data?: BusinessHealthResponse;
}

const InsightDetailDrawer = ({
    isOpen,
    onRequestClose,
    metricsGroup,
    scoreComponent,
    primaryMetrics,
    presentation,
    drawerMetricKeys,
    data,
}: InsightDetailDrawerProps) => {
    const labels = getLabels(scoreComponent, data);
    const groupMeta = METRICS_GROUPS_DEFINITIONS[metricsGroup];
    const title = labels?.label ?? groupMeta?.label ?? 'Metrics';
    const subtitle = labels?.description ?? groupMeta?.description ?? '';

    const groupedEntries = getGlobalMetricsByGroup(data?.metrics, metricsGroup);
    let aggregateRows: Array<[string, GlobalBusinessHealthMetric | undefined]>;
    if (drawerMetricKeys?.length) {
        aggregateRows = drawerMetricKeys.map((key) => [key, data?.metrics?.[key]]);
    } else if (groupedEntries.length > 0) {
        aggregateRows = groupedEntries;
    } else {
        aggregateRows = primaryMetrics.map((key) => [key, data?.metrics?.[key]]);
    }

    const channels = data?.channels ?? [];

    const aggregateCards = aggregateRows.filter(
        ([metricKey, snapshot]) => (
            !metricKey.startsWith('total_revenue_distribution_percentile_')
            && snapshot != null
            && Number.isFinite(snapshot.current)
        ),
    );

    const aggregateMetricsStack = (
        <DrawerMetricsStack>
            {aggregateCards.map(([metricKey, snapshot]) => (
                <DrawerMetricCard
                    key={metricKey}
                    metricKey={metricKey}
                    row={snapshot as GlobalBusinessHealthMetric}
                    variant="stack"
                    labels={getLabels(metricKey, data)}
                />
            ))}
        </DrawerMetricsStack>
    );

    const aggregateDrawerContent = aggregateCards.length === 0 ? (
        <EmptyHint>No metrics available for this group.</EmptyHint>
    ) : aggregateMetricsStack;

    const channelDrawerContent = channels.length === 0 ? (
        <EmptyHint>No channel data for this period.</EmptyHint>
    ) : (
        <>
            {channels.map((ch) => (
                <InsightChannelDrawerSection key={ch.channel} channel={ch} data={data} />
            ))}
        </>
    );

    return (
        <StyledDrawer
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            slideFrom="right"
            size="large"
            header={(
                <DrawerHeader>
                    <DrawerTitle>{title}</DrawerTitle>
                    {subtitle ? <DrawerSubtitle>{subtitle}</DrawerSubtitle> : null}
                </DrawerHeader>
            )}
        >
            <RenderIf isTrue={isOpen}>
                <DrawerBody>
                    {presentation === 'per_channel' ? (
                        <>
                            {aggregateCards.length > 0 ? (
                                <DrawerAggregateSection>
                                    {aggregateMetricsStack}
                                </DrawerAggregateSection>
                            ) : null}
                            {channelDrawerContent}
                        </>
                    ) : aggregateDrawerContent}
                </DrawerBody>
            </RenderIf>
        </StyledDrawer>
    );
};

export default InsightDetailDrawer;
